import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { Flame } from '@phosphor-icons/react';
import { toast } from 'sonner';

/**
 * Bharat Gas / BPCL Data Finder
 * - Client-side tool (standalone HTML in /public/bharat_gas_tool.html)
 * - Listens to postMessage from iframe to push data to Quick Booking (semi-auto)
 * - Access: Admin, Staff (incl. sub-staff), Assistant, Accountant, and
 *   Distributors whose oil_companies include Bharat/BPCL
 */
export default function BharatGasToolPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const frameRef = useRef(null);

  const role = user?.role;
  // ACCESS RESTRICTED: Only admin/owner + assistant + Bharat/BPCL distributor. Staff, Sub-staff, Accountant restricted.
  const isInternal = ['admin', 'owner', 'assistant'].includes(role);
  const canPushToQuickBooking = ['admin', 'owner', 'assistant'].includes(role);
  const oils = (user?.oil_companies || []).map((o) => String(o).toUpperCase());
  const isBharatDistributor = role === 'distributor' &&
    oils.some((o) => o.includes('BHARAT') || o.includes('BPCL'));

  const canAccess = isInternal || isBharatDistributor;

  useEffect(() => {
    if (frameRef.current) {
      frameRef.current.src = `/bharat_gas_tool.html?ts=${Date.now()}`;
    }
  }, []);

  useEffect(() => {
    const handler = async (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.source !== frameRef.current?.contentWindow) return;
      const msg = event?.data;
      if (!msg || msg.type !== 'BHARAT_GAS_SEND_TO_QUICK_BOOKING') return;
      if (!canPushToQuickBooking) {
        toast.error('Aap Quick Booking session create nahi kar sakte');
        return;
      }
      const items = Array.isArray(msg.items) ? msg.items : [];
      if (!items.length) {
        toast.error('No consumers received from tool');
        return;
      }
      try {
        // For semi-auto we need LPG IDs — tool sends mobile + consumer; compute 17-digit ID
        const lpg_ids = items.map((it) => {
          const c = String(it.consumer || '').replace(/\D/g, '');
          if (!c) return null;
          const zeros = Math.max(0, 16 - c.length);
          return '1' + '0'.repeat(zeros) + c;
        }).filter(Boolean);

        if (!lpg_ids.length) {
          toast.error('No valid consumer numbers');
          return;
        }

        const res = await api.post('/automation/semi-auto/create', {
          name: `${msg.agencyName || 'Bharat Gas'} — ${new Date().toLocaleDateString('en-IN')}`,
          provider: msg.provider || 'bharat',
          lpg_ids,
          parallel: 10,
          auto_assign_gift_cards: true,
        });
        toast.success(
          `✓ Quick Booking session created — ${res.data.total_tasks} tasks. Redirecting...`
        );
        setTimeout(() => navigate('/quick-booking?tab=semi-auto'), 1200);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Failed to create Quick Booking session');
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [canPushToQuickBooking, navigate]);

  if (!canAccess) {
    return (
      <div className="p-6 text-center text-red-600 font-medium" data-testid="bharat-access-denied">
        Access denied. Yeh tool sirf Bharat Gas / BPCL distributors aur Admin/Assistant ke liye hai.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="bharat-gas-tool-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Flame size={28} weight="duotone" className="text-orange-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Bharat Gas / BPCL — Data Finder</h1>
            <p className="text-sm text-gray-500">
              Excel/paste karke bookings filter karo aur seedha Quick Booking me send karo.
            </p>
          </div>
        </div>
        {canPushToQuickBooking && (
          <div className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg px-3 py-2">
            💡 Tool ke andar <b>"Send to Quick Booking"</b> button se direct semi-auto session ban jayegi
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" style={{ height: 'calc(100vh - 200px)', minHeight: 600 }}>
        <iframe
          ref={frameRef}
          title="Bharat Gas Booking Tool"
          className="w-full h-full border-0"
          sandbox="allow-scripts allow-same-origin allow-downloads allow-forms"
          data-testid="bharat-tool-iframe"
        />
      </div>
    </div>
  );
}
